'use client';

import {
	AlertTriangle,
	BadgeCheck,
	BookOpen,
	CheckCircle2,
	Clock3,
	GraduationCap,
	LifeBuoy,
	Megaphone,
	MessageSquareText,
	Plus,
	RefreshCw,
	UserCheck
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	AssignCustomerSuccessTicketRequest,
	CreateCustomerSuccessTicketRequest,
	CustomerSuccessCategory,
	CustomerSuccessChecklistItem,
	CustomerSuccessChecklistStatus,
	CustomerSuccessLifecycleInsight,
	CustomerSuccessLifecycleStage,
	CustomerSuccessPriority,
	CustomerSuccessRiskItem,
	CustomerSuccessRiskSeverity,
	CustomerSuccessSource,
	CustomerSuccessStatus,
	CustomerSuccessSummaryResponse,
	CustomerSuccessSupportChannel,
	CustomerSuccessTicketResponse,
	ResolveCustomerSuccessTicketRequest
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type TicketFormState = {
	category: CustomerSuccessCategory;
	priority: CustomerSuccessPriority;
	lifecycleStage: CustomerSuccessLifecycleStage;
	source: CustomerSuccessSource;
	title: string;
	description: string;
	requesterEmployeeId: string;
	dueDate: string;
};

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function defaultDueDate(): string {
	const date = new Date();
	date.setDate(date.getDate() + 3);
	return date.toISOString().slice(0, 10);
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { month: '2-digit', day: '2-digit' }).format(date);
}

function categoryLabel(category: CustomerSuccessCategory): string {
	const labels: Record<CustomerSuccessCategory, string> = {
		PRODUCT_GUIDE: '사용 가이드',
		UPDATE_NOTE: '업데이트 소식',
		ACADEMY: '아카데미',
		NOTICE: '공지사항',
		PRODUCT_INTERVIEW: '제품 인터뷰',
		TECHNICAL_SUPPORT: '기술 지원',
		BILLING: '결제',
		ONBOARDING: '온보딩'
	};
	return labels[category] ?? category;
}

function sourceLabel(source: CustomerSuccessSource): string {
	const labels: Record<CustomerSuccessSource, string> = {
		GUIDE: '가이드',
		UPDATE_NOTE: '업데이트',
		ACADEMY: '아카데미',
		NOTICE: '공지',
		PRODUCT_INTERVIEW: '인터뷰',
		IN_APP: '인앱',
		EMAIL: '이메일'
	};
	return labels[source] ?? source;
}

function priorityLabel(priority: CustomerSuccessPriority): string {
	const labels: Record<CustomerSuccessPriority, string> = {
		LOW: '낮음',
		MEDIUM: '보통',
		HIGH: '높음',
		URGENT: '긴급'
	};
	return labels[priority] ?? priority;
}

function statusLabel(status: CustomerSuccessStatus): string {
	const labels: Record<CustomerSuccessStatus, string> = {
		NEW: '신규',
		TRIAGED: '분류',
		IN_PROGRESS: '진행중',
		RESOLVED: '해결',
		CLOSED: '종료'
	};
	return labels[status] ?? status;
}

function statusVariant(status: CustomerSuccessStatus): 'success' | 'warning' | 'outline' {
	if (status === 'RESOLVED' || status === 'CLOSED') return 'success';
	if (status === 'NEW' || status === 'TRIAGED') return 'warning';
	return 'outline';
}

function checklistStatusLabel(status: CustomerSuccessChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: CustomerSuccessChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(
	severity: CustomerSuccessRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: CustomerSuccessChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="고객 성공 체크리스트가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: CustomerSuccessRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 지원 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">SLA 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function LifecycleTable({ insights }: { insights: CustomerSuccessLifecycleInsight[] }) {
	if (insights.length === 0) return <EmptyState message="라이프사이클별 지원 현황이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[560px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">단계</th>
						<th className="px-4 py-3 font-medium">진행</th>
						<th className="px-4 py-3 font-medium">해결</th>
						<th className="px-4 py-3 font-medium">상태</th>
					</tr>
				</thead>
				<tbody>
					{insights.map((insight) => (
						<tr key={insight.stage} className="border-t border-border">
							<td className="px-4 py-4">
								<p className="font-semibold">{insight.label}</p>
								<p className="mt-1 text-xs text-muted-foreground">{insight.stage}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{numberLabel(insight.openCount)}건</td>
							<td className="px-4 py-4 text-muted-foreground">{numberLabel(insight.resolvedCount)}건</td>
							<td className="px-4 py-4">
								<Badge variant={insight.healthLabel === '주의' ? 'warning' : 'outline'}>{insight.healthLabel}</Badge>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function ChannelIcon({ code }: { code: string }) {
	const Icon =
		code === 'ACADEMY'
			? GraduationCap
			: code === 'NOTICE' || code === 'UPDATE_NOTE'
				? Megaphone
				: code === 'PRODUCT_INTERVIEW'
					? MessageSquareText
					: BookOpen;
	return <Icon className="size-4" />;
}

function ChannelGrid({ channels }: { channels: CustomerSuccessSupportChannel[] }) {
	if (channels.length === 0) return <EmptyState message="등록된 지원 채널이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			{channels.map((channel) => (
				<div key={channel.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="flex items-start gap-3">
						<div className="rounded-md bg-muted p-2 text-muted-foreground">
							<ChannelIcon code={channel.code} />
						</div>
						<div className="min-w-0">
							<p className="font-semibold">{channel.label}</p>
							<p className="mt-1 text-sm text-muted-foreground">{channel.description}</p>
							<Badge className="mt-3" variant="outline">
								{channel.actionLabel}
							</Badge>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

function TicketsTable({
	tickets,
	actionId,
	onAssign,
	onResolve
}: {
	tickets: CustomerSuccessTicketResponse[];
	actionId: number | null;
	onAssign: (ticket: CustomerSuccessTicketResponse) => void;
	onResolve: (ticket: CustomerSuccessTicketResponse) => void;
}) {
	if (tickets.length === 0) return <EmptyState message="등록된 지원 티켓이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[760px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">티켓</th>
						<th className="px-4 py-3 font-medium">담당</th>
						<th className="px-4 py-3 font-medium">기한</th>
						<th className="px-4 py-3 font-medium">상태</th>
						<th className="px-4 py-3 font-medium">작업</th>
					</tr>
				</thead>
				<tbody>
					{tickets.map((ticket) => (
						<tr key={ticket.id} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{ticket.title}</p>
									<Badge variant={ticket.priority === 'URGENT' ? 'destructive' : 'outline'}>
										{priorityLabel(ticket.priority)}
									</Badge>
								</div>
								<p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{ticket.description}</p>
								<p className="mt-2 text-xs text-muted-foreground">
									{categoryLabel(ticket.category)} · {sourceLabel(ticket.source)} · 요청자 #{ticket.requesterEmployeeId}
								</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{ticket.ownerName ?? '미배정'}</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(ticket.dueDate)}</td>
							<td className="px-4 py-4">
								<Badge variant={statusVariant(ticket.status)}>{statusLabel(ticket.status)}</Badge>
							</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										size="sm"
										variant="outline"
										disabled={actionId === ticket.id || ticket.status === 'RESOLVED' || ticket.status === 'CLOSED'}
										onClick={() => onAssign(ticket)}
									>
										<UserCheck className="size-4" />
										배정
									</Button>
									<Button
										type="button"
										size="sm"
										disabled={actionId === ticket.id || ticket.status === 'RESOLVED' || ticket.status === 'CLOSED'}
										onClick={() => onResolve(ticket)}
									>
										<BadgeCheck className="size-4" />
										해결
									</Button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function CustomerSuccessPage() {
	const [state, setState] = useState<RequestState<CustomerSuccessSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [ticketActionId, setTicketActionId] = useState<number | null>(null);
	const [form, setForm] = useState<TicketFormState>({
		category: 'TECHNICAL_SUPPORT',
		priority: 'HIGH',
		lifecycleStage: 'ADOPTION',
		source: 'IN_APP',
		title: '',
		description: '',
		requesterEmployeeId: '',
		dueDate: defaultDueDate()
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<CustomerSuccessSummaryResponse>('/customer-success/summary');
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitTicket(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('ticket');
		setActionMessage(null);
		try {
			const request: CreateCustomerSuccessTicketRequest = {
				category: form.category,
				priority: form.priority,
				lifecycleStage: form.lifecycleStage,
				source: form.source,
				title: form.title.trim(),
				description: form.description.trim(),
				requesterEmployeeId: Number(form.requesterEmployeeId),
				dueDate: form.dueDate || null
			};
			await api.post('/customer-success/tickets', request);
			setForm((current) => ({
				...current,
				title: '',
				description: '',
				requesterEmployeeId: '',
				dueDate: defaultDueDate()
			}));
			setActionMessage('지원 티켓을 등록했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function assignTicket(ticket: CustomerSuccessTicketResponse) {
		setTicketActionId(ticket.id);
		setActionMessage(null);
		try {
			const request: AssignCustomerSuccessTicketRequest = {
				ownerName: ticket.ownerName ?? 'Customer Success Manager',
				dueDate: ticket.dueDate ?? defaultDueDate()
			};
			await api.patch(`/customer-success/tickets/${ticket.id}/assign`, request);
			setActionMessage('지원 담당자를 배정했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setTicketActionId(null);
		}
	}

	async function resolveTicket(ticket: CustomerSuccessTicketResponse) {
		setTicketActionId(ticket.id);
		setActionMessage(null);
		try {
			const request: ResolveCustomerSuccessTicketRequest = {
				resolutionSummary: `${ticket.title} 지원 결과를 고객 성공 화면에서 반영했습니다.`
			};
			await api.patch(`/customer-success/tickets/${ticket.id}/resolve`, request);
			setActionMessage('지원 티켓을 해결 처리했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setTicketActionId(null);
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="고객 성공"
				description="사용 가이드, 업데이트, 아카데미, 공지, 제품 인터뷰와 지원 티켓을 한 흐름으로 운영합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="진행 티켓"
							value={data.openTicketCount}
							description={`전체 ${numberLabel(data.totalTicketCount)}건`}
							icon={LifeBuoy}
						/>
						<StatCard
							title="긴급·초과"
							value={`${numberLabel(data.urgentTicketCount)} / ${numberLabel(data.overdueTicketCount)}`}
							description="긴급 / SLA 초과"
							icon={AlertTriangle}
						/>
						<StatCard
							title="미배정"
							value={data.unassignedTicketCount}
							description={`해결 ${numberLabel(data.resolvedTicketCount)}건`}
							icon={UserCheck}
						/>
						<StatCard
							title="평균 해결"
							value={`${numberLabel(data.averageResolutionDays)}일`}
							description="고객 성공 처리 속도"
							icon={Clock3}
						/>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>지원 티켓 등록</CardTitle>
							<CardDescription>가이드 보강, 업데이트 안내, 교육, 공지, 제품 피드백 요청을 운영 티켓으로 남깁니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<form className="grid gap-3 xl:grid-cols-[1fr_1fr_1fr_auto]" onSubmit={submitTicket}>
								<div className="grid gap-3 sm:grid-cols-2 xl:col-span-2">
									<div>
										<Label htmlFor="customer-success-category">카테고리</Label>
										<select
											id="customer-success-category"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.category}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													category: event.target.value as CustomerSuccessCategory
												}))
											}
										>
											<option value="TECHNICAL_SUPPORT">기술 지원</option>
											<option value="PRODUCT_GUIDE">사용 가이드</option>
											<option value="UPDATE_NOTE">업데이트 소식</option>
											<option value="ACADEMY">아카데미</option>
											<option value="NOTICE">공지사항</option>
											<option value="PRODUCT_INTERVIEW">제품 인터뷰</option>
											<option value="BILLING">결제</option>
											<option value="ONBOARDING">온보딩</option>
										</select>
									</div>
									<div>
										<Label htmlFor="customer-success-title">제목</Label>
										<Input
											id="customer-success-title"
											value={form.title}
											onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
											required
										/>
									</div>
									<div>
										<Label htmlFor="customer-success-priority">우선순위</Label>
										<select
											id="customer-success-priority"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.priority}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													priority: event.target.value as CustomerSuccessPriority
												}))
											}
										>
											<option value="LOW">낮음</option>
											<option value="MEDIUM">보통</option>
											<option value="HIGH">높음</option>
											<option value="URGENT">긴급</option>
										</select>
									</div>
									<div>
										<Label htmlFor="customer-success-lifecycle">라이프사이클</Label>
										<select
											id="customer-success-lifecycle"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.lifecycleStage}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													lifecycleStage: event.target.value as CustomerSuccessLifecycleStage
												}))
											}
										>
											<option value="ONBOARDING">온보딩</option>
											<option value="ADOPTION">활성화</option>
											<option value="EXPANSION">확장</option>
											<option value="RENEWAL">갱신</option>
											<option value="RISK">위험</option>
										</select>
									</div>
									<div>
										<Label htmlFor="customer-success-source">접점</Label>
										<select
											id="customer-success-source"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={form.source}
											onChange={(event) =>
												setForm((current) => ({
													...current,
													source: event.target.value as CustomerSuccessSource
												}))
											}
										>
											<option value="IN_APP">인앱</option>
											<option value="EMAIL">이메일</option>
											<option value="GUIDE">가이드</option>
											<option value="UPDATE_NOTE">업데이트</option>
											<option value="ACADEMY">아카데미</option>
											<option value="NOTICE">공지</option>
											<option value="PRODUCT_INTERVIEW">인터뷰</option>
										</select>
									</div>
									<div>
										<Label htmlFor="customer-success-requester">요청자 직원 ID</Label>
										<Input
											id="customer-success-requester"
											type="number"
											value={form.requesterEmployeeId}
											onChange={(event) =>
												setForm((current) => ({ ...current, requesterEmployeeId: event.target.value }))
											}
											required
										/>
									</div>
									<div>
										<Label htmlFor="customer-success-due-date">응답기한</Label>
										<Input
											id="customer-success-due-date"
											type="date"
											value={form.dueDate}
											onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))}
										/>
									</div>
								</div>
								<div className="xl:col-span-1">
									<Label htmlFor="customer-success-description">상세 내용</Label>
									<textarea
										id="customer-success-description"
										className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-28 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
										value={form.description}
										onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
										required
									/>
								</div>
								<div className="flex items-end">
									<Button type="submit" className="w-full" disabled={operationLoading === 'ticket'}>
										<Plus className="size-4" />
										티켓 등록
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>고객 성공 체크리스트</CardTitle>
								<CardDescription>가이드, 업데이트, 교육, 공지, 피드백 루프가 운영 가능한 상태인지 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChecklistGrid items={data.checklist} />
							</CardContent>
						</Card>
						<RiskPanel risks={data.riskItems} />
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>라이프사이클 현황</CardTitle>
								<CardDescription>온보딩부터 갱신·위험 고객까지 지원 흐름을 나눠 봅니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<LifecycleTable insights={data.lifecycleInsights} />
							</CardContent>
						</Card>
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>지원 채널</CardTitle>
								<CardDescription>상용 서비스에 필요한 고객 지원 콘텐츠와 피드백 채널을 운영합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChannelGrid channels={data.supportChannels} />
							</CardContent>
						</Card>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>최근 지원 티켓</CardTitle>
							<CardDescription>담당자 배정과 해결 처리를 바로 실행합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<TicketsTable
								tickets={data.recentTickets}
								actionId={ticketActionId}
								onAssign={(ticket) => void assignTicket(ticket)}
								onResolve={(ticket) => void resolveTicket(ticket)}
							/>
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
